/**
 * Shared Dentally utility functions.
 * Shared Dentally utility functions for practitioner names, formatting, etc.
 */

// Parse Dentally "DD-MM-YYYY HH:mm:ss.SSSSSSZ" to native Date object
export const parseDentallyDate = (dateStr) => {
  if (!dateStr) return null;
  const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})\s*(.*)?$/);
  if (match) {
    const [, day, month, year, timePart] = match;
    const d = new Date(`${year}-${month}-${day}T${timePart || '00:00:00'}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  try {
    const d = new Date(dateStr);
    if (!Number.isNaN(d.getTime())) return d;
  } catch { /* ignore */ }
  return null;
};

// Dentally's practitioner-diary (rota) feed is the one Dentally feed that is NOT a
// UTC instant feed. It emits practice-local WALL-CLOCK times carrying a spurious
// `Z` designator: a shift that really starts 09:00 local is sent as
// "22-09-2026 09:00:00.000000Z" in both January and July. Reading the `Z` as UTC
// renders every rota an hour late for the seven months of British Summer Time.
//
// Only diary `start_time` / `end_time` (top level and inside `breaks`) are
// wall-clock. Availability slots, appointments, `updated_at` and `last_login` are
// genuine instants and MUST keep going through `parseDentallyDate`, which reads
// the designator. Diary `day` is a bare date with no time part and is unaffected.
//
// The suffix is only stripped when it directly follows a clock time, so a
// date-only legacy value such as "22-09-2026" is never mangled.
const WALL_CLOCK_ZONE_SUFFIX = /(\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)\s*(?:Z|[+-]\d{2}:?\d{2})$/i;

export const parseDentallyWallClockDate = (dateStr) => {
  if (!dateStr) return null;
  const raw = String(dateStr).trim();
  const local = raw.replace(WALL_CLOCK_ZONE_SUFFIX, '$1');
  return parseDentallyDate(local) || parseDentallyDate(dateStr);
};

// The appointment `on` / `before` / `after` query params are DRF DateFields
// (apps/dentally/serializers.py, DentallyListAppointmentsSerializer). Sending
// `toISOString()` gives them a full datetime and the request is rejected with a
// 400 before it reaches Dentally. `toISOString()` is also the wrong DATE: it
// converts to UTC, so a picker set to 1 October sends 30 September for any BST
// local time before 01:00. This formats the picker's own local calendar date,
// which is what the user chose and what the field accepts.
//
// Only date-only params need this. `updated_after` and `created_after` are
// DentallyDateTimeFields and must keep sending a real instant via toISOString().
export const toDentallyDateParam = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DATE_ONLY_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DATE_ONLY_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isLeapYear = (year) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const validDateParts = (year, month, day) => {
  if (!Number.isInteger(year) || year < 1 || year > 9999) return false;
  if (!Number.isInteger(month) || month < 1 || month > 12) return false;
  const maxDay = month === 2 && isLeapYear(year) ? 29 : DATE_ONLY_DAYS[month - 1];
  return Number.isInteger(day) && day >= 1 && day <= maxDay;
};

export const formatDentallyDateOnly = (value, { allowLegacy = false, invalidFallback = value } = {}) => {
  if (value == null || value === '') return null;
  const raw = String(value);
  let match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  let parts = match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null;
  if (!parts && allowLegacy) {
    match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(raw);
    if (match) parts = [Number(match[3]), Number(match[2]), Number(match[1])];
  }
  if (!parts || !validDateParts(...parts)) return invalidFallback;
  const [year, month, day] = parts;
  return `${String(day).padStart(2, '0')} ${DATE_ONLY_MONTHS[month - 1]} ${String(year).padStart(4, '0')}`;
};

// Format Dentally datetime to readable string
export const formatDentallyDate = (dateStr, includeTime = false) => {
  const d = parseDentallyDate(dateStr);
  if (!d) return dateStr || null;
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  if (includeTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
  return d.toLocaleDateString('en-GB', opts);
};

// Format diary time "HH:mm". Diary times are wall-clock, not UTC instants.
export const formatDiaryTime = (dateStr) => {
  const d = parseDentallyWallClockDate(dateStr);
  if (!d) return dateStr || '-';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Format diary day "Mon, 02 Jan 2025"
export const formatDiaryDay = (dayStr) => {
  if (!dayStr) return '-';
  const d = parseDentallyDate(dayStr);
  if (!d) return dayStr;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

// Compute diary entry duration in minutes
// Duration is derived from the same wall-clock reading that is rendered, so the
// "9h" label always agrees with the "09:00 - 18:00" above it.
export const getDiaryDurationMins = (entry) => {
  const s = parseDentallyWallClockDate(entry.start_time);
  const e = parseDentallyWallClockDate(entry.end_time);
  if (!s || !e) return null;
  return Math.round((e - s) / 60000);
};

// Format duration minutes to "Xh Ym" string
export const formatDurationLabel = (mins) => {
  if (!mins && mins !== 0) return '-';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

// Get practitioner display name from a Dentally practitioner object
export const availPractitionerName = (p) => {
  const u = p.user || {};
  return [u.title, u.first_name, u.last_name].filter(Boolean).join(' ') || `Practitioner #${p.id}`;
};

// Static fallback list of Dentally appointment reasons.
// Prefer useDentallyReasonOptions() hook which fetches the practice's actual reasons.
export const DENTALLY_REASON_OPTIONS = [
  // Core exam
  'Exam',
  'New Patient Exam',
  'NHS Exam',
  // Hygiene
  'Scale & Polish',
  'Hygienist',
  'Exam + Scale & Polish',
  // Continuing / follow-up
  'Continuing Treatment',
  'Review',
  'Follow Up',
  // Consultations
  'Consultation',
  'Implant Consultation',
  'Orthodontic Consultation',
  'Cosmetic Consultation',
  'Invisalign Consultation',
  // Restorative
  'Restoration',
  'Crown',
  'Bridge',
  'Denture',
  'Veneer',
  // Surgical / endodontic
  'Extraction',
  'Root Canal Treatment',
  'Endodontic',
  'Periodontal Treatment',
  // Cosmetic / other
  'Whitening',
  'Implant',
  'Emergency',
  'Other',
];
